import React, { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Search, X } from 'lucide-react';
import { SearchDropdown } from './SearchDropdown';

interface MobileSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

export function MobileSearch({ open, onOpenChange, triggerRef }: MobileSearchProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<{ height: number; top: number } | null>(null);

  // Keep results inside the visible area when a phone's keyboard opens.
  useEffect(() => {
    if (!open) return;
    const visibleViewport = window.visualViewport;
    if (!visibleViewport) return;
    const update = () => setViewport({ height: visibleViewport.height, top: visibleViewport.offsetTop });
    update();
    visibleViewport.addEventListener('resize', update);
    visibleViewport.addEventListener('scroll', update);
    return () => {
      visibleViewport.removeEventListener('resize', update);
      visibleViewport.removeEventListener('scroll', update);
      setViewport(null);
    };
  }, [open]);

  return <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay className="unique-mobile-search__overlay" />
      <Dialog.Content ref={contentRef} className="unique-mobile-search" style={viewport ? { height: viewport.height, top: viewport.top } : undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const panel = contentRef.current;
          const trigger = triggerRef.current;
          if (panel && trigger && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            const bounds = panel.getBoundingClientRect();
            const origin = trigger.getBoundingClientRect();
            const inset = [
              Math.max(0, origin.top - bounds.top), Math.max(0, bounds.right - origin.right),
              Math.max(0, bounds.bottom - origin.bottom), Math.max(0, origin.left - bounds.left),
            ].map((value) => `${value}px`).join(' ');
            panel.animate([
              { clipPath: `inset(${inset} round 28px)` },
              { clipPath: 'inset(0px 0px 0px 0px round 0px)' },
            ], { duration: 480, easing: 'cubic-bezier(.22, 1, .36, 1)' });
          }
          inputRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => { event.preventDefault(); triggerRef.current?.focus({ preventScroll: true }); }}>
        <div className="unique-mobile-search__heading">
          <Dialog.Title>Encontre seu próximo evento</Dialog.Title>
          <Dialog.Close className="unique-icon-button" aria-label="Fechar busca"><X size={21} /></Dialog.Close>
        </div>
        <Dialog.Description className="unique-mobile-search__description">Busque pelo nome do evento ou pela cidade.</Dialog.Description>
        <form role="search" className="unique-mobile-search__form" onSubmit={(event) => { event.preventDefault(); inputRef.current?.blur(); }}>
          <div className="unique-header__field">
            <Search size={19} aria-hidden="true" />
            <input ref={inputRef} type="search" enterKeyHint="search" autoComplete="off" aria-label="Buscar eventos ou cidades"
              placeholder="Evento ou cidade" value={query} onChange={(event) => setQuery(event.target.value)} />
            {query && <button type="button" aria-label="Limpar busca" onClick={() => { setQuery(''); inputRef.current?.focus(); }}><X size={18} /></button>}
          </div>
        </form>
        <SearchDropdown query={query} isOpen={open} onClose={() => onOpenChange(false)} embedded />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
